import type { Company, Employee, RosterDailyResultsDTO, TimeBalance, TimeEntry } from "@pentacode/core";
import {
    TimeEntryType,
    createAbsenceHoursGetter,
    getNominalTime,
    getTimeResult,
    makeEmptyTimeResult,
} from "@pentacode/core";
import type { DateString, Hours } from "@pentacode/openapi";
import { add, subtract } from "@pentacode/openapi";

function getBalanceChain(row: RosterDailyResultsDTO) {
    return row.timeBalancePlanned ?? row.timeBalance;
}

function isFinalWorkEntry(entry: TimeEntry, from: DateString, to: DateString): boolean {
    return entry.date >= from && entry.date < to && entry.type === TimeEntryType.Work && !!entry.final;
}

function getFinalVsPlannedDelta(entry: TimeEntry): Hours {
    return subtract(entry.result?.base.duration ?? (0 as Hours), entry.resultPlanned?.base.duration ?? (0 as Hours));
}

function getFinalVsPlannedCorrection(entries: TimeEntry[], from: DateString, to: DateString): Hours {
    return entries
        .filter((entry) => isFinalWorkEntry(entry, from, to))
        .reduce((total, entry) => add(total, getFinalVsPlannedDelta(entry)), 0 as Hours);
}

function getEndOfWeekBalance(
    rows: RosterDailyResultsDTO[],
    from: DateString,
    to: DateString,
    today: DateString,
    correction: Hours
): Hours | null {
    const toRow = rows.find((d) => d.date === to);
    if (!toRow) {
        return null;
    }
    if (today <= from) {
        return add(getBalanceChain(toRow).carry, correction);
    }
    const anchorDate = today < to ? today : to;
    const anchorRow = rows.find((d) => d.date === anchorDate);
    if (!anchorRow) {
        return add(getBalanceChain(toRow).carry, correction);
    }
    return add(
        add(anchorRow.timeBalance.carry, subtract(getBalanceChain(toRow).carry, getBalanceChain(anchorRow).carry)),
        correction
    );
}

export function calculateTimeBalance(
    company: Company,
    employee: Employee,
    entries: TimeEntry[],
    dailyResults: RosterDailyResultsDTO[],
    from: DateString,
    to: DateString,
    today: DateString,
    entriesFromToday: TimeEntry[] = entries.filter((e) => e.date >= today)
): TimeBalance {
    const employeeResults = dailyResults.filter((d) => d.employeeId === employee.id);
    const fromRow = employeeResults.find((d) => d.date === from);

    const reset = fromRow && getBalanceChain(fromRow).reset;
    const carry = (fromRow && getBalanceChain(fromRow).carry) || (0 as Hours);

    const balanceCorrection =
        today < to ? getFinalVsPlannedCorrection(entriesFromToday, reset == null ? today : from, to) : (0 as Hours);
    const carryCorrection = today < from ? getFinalVsPlannedCorrection(entriesFromToday, today, from) : (0 as Hours);

    const timeBalance = {
        employeeId: employee.id,
        from,
        to,
        actual: 0 as Hours,
        nominal: 0 as Hours,
        reset,
        carry: reset == null ? add(carry, carryCorrection) : carry,
        adjustments: 0 as Hours,
        balance: 0 as Hours,
        absences: 0 as Hours,
        work: 0 as Hours,
        difference: 0 as Hours,
    };

    timeBalance.nominal = getNominalTime(
        company.settings,
        company,
        employee,
        { from, to },
        createAbsenceHoursGetter(employeeResults)
    );
    timeBalance.actual = entries.reduce((total, entry) => {
        const result =
            entry.type === TimeEntryType.Work
                ? getTimeResult(company.settings, company, employee, entry, makeEmptyTimeResult(), 1, "mixed")
                : entry.result;
        return add(total, result?.base.duration || (0 as Hours));
    }, 0 as Hours);
    timeBalance.difference = subtract(timeBalance.actual, timeBalance.nominal);
    timeBalance.balance =
        getEndOfWeekBalance(employeeResults, from, to, today, balanceCorrection) ??
        add(timeBalance.reset ?? timeBalance.carry, timeBalance.difference);

    return timeBalance;
}
